import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import WomanReport from '../../../../components/icons/WomanReport'
import BehavioralAssessment from '../../../../components/BehavioralAssessment'
import CommunicationAssessment from '../../../../components/CommunicationAssessment'
import AptitudeAssessment from '../../../../components/AptitudeAssessment' // NEW: Import AptitudeAssessment
import AIInterviewReportCard from './AIInterviewReportCard'
import { fetchReports } from '../../actions'
import selectors from '../../selectors'

const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  laptop: '1024px',
}

const ReportContainer = styled.div`
  width: 100%;
  margin: 88px auto 24px;

  @media (max-width: ${breakpoints.tablet}) {
    margin: 40px auto 24px;
  }
`

const HeaderSection = styled.div`
  width: 100%;
  height: 134px;
  background: #f7f7f7;
  border-radius: 25px;
  display: flex;
  align-items: left;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 32px;
  margin-top: 64px;

  @media (max-width: ${breakpoints.tablet}) {
    height: auto;
    padding: 24px 16px;
    border-radius: 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    margin-bottom: 24px;
  }
`

const IconSection = styled.div`
  position: absolute;
  left: 70px;
  bottom: 0;
  width: 130px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }

  svg {
    width: 100%;
    height: auto;
  }
`

const ContentSection = styled.div`
  margin-left: 276px;
  flex: 1;

  @media (max-width: ${breakpoints.tablet}) {
    margin-left: 0;
  }
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #343e59;
  margin: 0 0 12px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 20px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`

const Description = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 800px;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 14px;
  }
`

const Button = styled.button`
  background: ${props =>
    props.variant === 'text' ? 'transparent' : '#007AFF'};
  color: ${props => (props.variant === 'text' ? '#007AFF' : 'white')};
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  margin-top: 16px;
  text-decoration: ${props =>
    props.variant === 'text' ? 'underline' : 'none'};

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  &:hover {
    background: ${props =>
      props.variant === 'text' ? 'transparent' : '#0056b3'};
    color: ${props => (props.variant === 'text' ? '#0056b3' : 'white')};
  }
`

const OlderReportSection = styled.div`
  margin-top: 24px;
  border-top: 1px solid #e5e5e5;

  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 20px;
    padding-top: 20px;
  }
`

const Reports = ({
  reports,
  loading,
  error,
  fetchReports,
  completedAssessments,
}) => {
  const [showOlderBehavioral, setShowOlderBehavioral] = useState(false)
  const [showOlderCommunication, setShowOlderCommunication] = useState(false)
  const [showOlderAptitude, setShowOlderAptitude] = useState(false)

  useEffect(() => {
    if (completedAssessments?.length > 0) {
      fetchReports()
    }
  }, [fetchReports, completedAssessments])

  // Early return if no reports or error
  if (loading) {
    return <div>Loading reports...</div>
  }

  if (error) {
    return <div>Error loading reports: {error}</div>
  }

  // Check if reports is an array and has items
  if (!Array.isArray(reports) || reports.length === 0) {
    return (
      <ReportContainer>
        <HeaderSection>
          <IconSection>
            <WomanReport />
          </IconSection>
          <ContentSection>
            <Title>Assessment Reports</Title>
            <Description>
              No reports available yet. Complete an assessment to see your
              results here.
            </Description>
          </ContentSection>
        </HeaderSection>
      </ReportContainer>
    )
  }

  // Filter out corporate assessments and sort by timestamp (most recent first)
  const nonCorporateReports = reports
    .filter(report => !report.isCorporate)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  
  // If no non-corporate reports available after filtering
  if (nonCorporateReports.length === 0) {
    return (
      <ReportContainer>
        <HeaderSection>
          <IconSection>
            <WomanReport />
          </IconSection>
          <ContentSection>
            <Title>Assessment Reports</Title>
            <Description>
              No institute assessment reports available yet. Complete an institute assessment to see your
              results here.
            </Description>
          </ContentSection>
        </HeaderSection>
      </ReportContainer>
    )
  }

  // Group reports by type and get latest 2 for each type
  const communicationReports = nonCorporateReports
    .filter(report => report.type.toLowerCase() === 'communication')
    .slice(0, 2) // Get only the latest 2
    
  const behavioralReports = nonCorporateReports
    .filter(report => report.type.toLowerCase() === 'behavior')
    .slice(0, 2) // Get only the latest 2
    
  const aptitudeReports = nonCorporateReports
    .filter(report => report.type.toLowerCase() === 'aptitude')
    .slice(0, 2) // Get only the latest 2

  const isAIInterviewType = (t) => {
    const s = (t || '').toLowerCase()
    return s.includes('ai_interview') || s.includes('aiinterview') || s === 'ai interview'
  }
  const aiInterviewReports = nonCorporateReports
    .filter(r => isAIInterviewType(r.type))
    .slice(0, 5)

  // Get latest and older reports for each type
  const latestCommunication = communicationReports[0]
  const olderCommunication = communicationReports[1]
  const hasOlderCommunication = communicationReports.length > 1

  const latestBehavioral = behavioralReports[0]
  const olderBehavioral = behavioralReports[1]
  const hasOlderBehavioral = behavioralReports.length > 1

  const latestAptitude = aptitudeReports[0]
  const olderAptitude = aptitudeReports[1]
  const hasOlderAptitude = aptitudeReports.length > 1

  const renderReport = report => {
    if (!report) return null

    // AI Interview reports come through here with type='AI_Interview' (or similar).
    // Bypass the switch since the data shape is parameter-driven, not section-based.
    if (isAIInterviewType(report.type)) {
      return <AIInterviewReportCard report={report} />
    }

    switch (report.type.toLowerCase()) {
      case 'behavior':
        return (
          <BehavioralAssessment
            data={report}
            categories={report.categories}
            timestamp={report.timestamp}
            assessmentId={report.id}
          />
        )
      case 'communication':
        return (
          <CommunicationAssessment
            data={{
              timestamp: report.timestamp,
              englishLang: report.speakingScore,
              readingAbility: report.readingScore,
              comprehension: report.listeningScore,
              writingScore: report.writingScore,
              overallScore: report.overallScore,
              cefrLevel: report.cefrLevel,
              sectionScores: {
                paragraphReading: report.paragraphReadingScore,
                audioQuestion: report.audioQuestionScore,
                videoResponse: report.videoResponseScore,
                questionBasedResponse: report.questionBasedResponseScore,
                emailWriting: report.emailWritingScore,
                dictation: report.dictationScore,
                sentenceCompletion: report.sentenceCompletionScore,
                sentenceBuild: report.sentenceBuildScore
              }
            }}
          />
        )
      case 'aptitude':
        return (
          <AptitudeAssessment
            data={report}
          />
        )
      default:
        return null
    }
  }

  return (
    <ReportContainer>
      <HeaderSection>
        <IconSection>
          <WomanReport />
        </IconSection>
        <ContentSection>
          <Title>Assessment Reports</Title>
          <Description>
            View your assessment results and track your progress over time.
          </Description>
        </ContentSection>
      </HeaderSection>

      {/* AI Interview Section */}
      {aiInterviewReports.length > 0 && (
        <>
          {aiInterviewReports.map((r, i) => (
            <div key={r.id || r.sessionId || i} style={{ marginBottom: 24 }}>
              {renderReport(r)}
            </div>
          ))}
        </>
      )}

      {/* Communication Assessment Section */}
      {latestCommunication && (
        <>
          {renderReport(latestCommunication)}
          {hasOlderCommunication && !showOlderCommunication && (
            <Button
              variant="text"
              onClick={() => setShowOlderCommunication(true)}
            >
              See older communication scores
            </Button>
          )}
          {showOlderCommunication && olderCommunication && (
            <OlderReportSection>
              {renderReport(olderCommunication)}
              <Button
                variant="text"
                onClick={() => setShowOlderCommunication(false)}
              >
                Hide older communication scores
              </Button>
            </OlderReportSection>
          )}
        </>
      )}

      {/* Behavioral Assessment Section */}
      {latestBehavioral && (
        <>
          {renderReport(latestBehavioral)}
          {hasOlderBehavioral && !showOlderBehavioral && (
            <Button variant="text" onClick={() => setShowOlderBehavioral(true)}>
              See older behavioral scores
            </Button>
          )}
          {showOlderBehavioral && olderBehavioral && (
            <OlderReportSection>
              {renderReport(olderBehavioral)}
              <Button
                variant="text"
                onClick={() => setShowOlderBehavioral(false)}
              >
                Hide older behavioral scores
              </Button>
            </OlderReportSection>
          )}
        </>
      )}

      {/* Aptitude Assessment Section */}
      {latestAptitude && (
        <>
          {renderReport(latestAptitude)}
          {hasOlderAptitude && !showOlderAptitude && (
            <Button variant="text" onClick={() => setShowOlderAptitude(true)}>
              See older aptitude scores
            </Button>
          )}
          {showOlderAptitude && olderAptitude && (
            <OlderReportSection>
              {renderReport(olderAptitude)}
              <Button
                variant="text"
                onClick={() => setShowOlderAptitude(false)}
              >
                Hide older aptitude scores
              </Button>
            </OlderReportSection>
          )}
        </>
      )}
    </ReportContainer>
  )
}

const mapStateToProps = state => ({
  reports: selectors.getReports(state),
  loading: selectors.getReportsLoading(state),
  error: selectors.getReportsError(state),
  completedAssessments: selectors.getCompletedAssessments(state),
})

const mapDispatchToProps = {
  fetchReports,
}

export default connect(mapStateToProps, mapDispatchToProps)(Reports)
